import { useBrandAnswer } from "@fox1ck-org/auth/saas/react";
import { Button, PageState } from "@fox1ck-org/ui";
import { Plus, Sparkles, Upload } from "lucide-react";

import { WriteOnly } from "@/components/auth/write-only";
import { useT, type TKey } from "@/lib/i18n";

import type { CreateSource } from "./create";
import { LANDER_TYPES, typeLabelKey } from "./kinds";
import { LanderSketch } from "./sketch";
import { LanderType } from "@gen/tracker/v1/lander_pb";

const ABOUT: Record<LanderType, TKey> = {
  [LanderType.UNSPECIFIED]: "landers.empty.about.landing",
  [LanderType.LANDING]: "landers.empty.about.landing",
  [LanderType.PRELANDING]: "landers.empty.about.prelanding",
  [LanderType.VSL]: "landers.empty.about.vsl",
  [LanderType.WHITE_PAGE]: "landers.empty.about.white",
  [LanderType.THANK_YOU]: "landers.empty.about.thanks",
};

/** The order the board explains them in: what a person meets first. */
const ORDER = [LanderType.PRELANDING, LanderType.LANDING, LanderType.VSL, LanderType.WHITE_PAGE, LanderType.THANK_YOU].filter((x) => LANDER_TYPES.includes(x));

/** Board LanderEmpty: no lander yet — the three ways to bring one, and what the five kinds of page are. */
export function LanderEmpty({ onCreate }: { onCreate: (source: CreateSource) => void }) {
  const t = useT();
  // The brand's motif over the first lander (board TEdges); the upload icon when the brand has none.
  const { motif } = useBrandAnswer();
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-8 overflow-y-auto">
      <PageState
        mode="empty"
        motif={motif}
        icon={<Upload className="size-5" strokeWidth={1.5} />}
        title={t("landers.empty.title")}
        description={t("landers.empty.body")}
        action={
          <WriteOnly>
            <span className="flex items-center gap-2">
              <Button onClick={() => onCreate("zip")}>
                <Plus className="size-4" strokeWidth={2} />
                {t("landers.new")}
              </Button>
              <Button variant="secondary" onClick={() => onCreate("studio")}>
                <Sparkles className="size-4" strokeWidth={1.5} />
                {t("landers.empty.studio")}
              </Button>
            </span>
          </WriteOnly>
        }
      />
      <section className="w-full max-w-295">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("landers.empty.kinds")}</h2>
        <div className="grid grid-cols-5 gap-3">
          {ORDER.map((type) => (
            <div key={type} className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3">
              <span className="block h-30 overflow-hidden rounded-md border border-border">
                <LanderSketch type={type} />
              </span>
              <b className="text-sm font-medium text-foreground">{t(typeLabelKey(type))}</b>
              <span className="text-xs leading-relaxed text-muted-foreground">{t(ABOUT[type])}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
